import type { UserDerivedKey } from "deso-protocol";
import { identity } from "deso-protocol";

import { waitForTxn } from "@/backend/api-helpers";
import { authorizeDerivedKeyAndWait } from "@/backend/focus";
import {
  createDerivedKey,
  DerivedKeyType,
  updateSubscriptionTier,
} from "@/backend/focus";
import type {
  CoreAccountFieldsFragment,
  PaidContentFragment,
  TierFragment,
} from "@/graphql/codegen/graphql";
import {
  APP_NAME_GENERIC,
  MIN_DESO_NANOS_FOR_SUBSCRIPTION_FEES,
} from "@/utils/constants";
import { FocusDateParser as fdp } from "@/utils/date";
import { parseError } from "@/utils/helpers";
import type { PartialAccount } from "@/utils/types";

export enum AccessGroupType {
  Subscription = "sub",
  OneTime = "one-time",
}
export const generateTierAccessGroupKeyName = (
  type: AccessGroupType = AccessGroupType.Subscription,
) => {
  const uuid = crypto.randomUUID().replace(/-/g, "");
  // The key name is limited to 32 characters.  the truncated uuid increases the
  // likelihood of collisions, but it should be okay since access groups are
  // unique by owner public key AND key name.
  return `focus-${type}-tier-${uuid}`.slice(0, 32);
};

export const getAccessGroupType = (accessGroupKeyName: string) => {
  if (accessGroupKeyName.startsWith("focus-sub-tier-")) {
    return AccessGroupType.Subscription;
  } else if (accessGroupKeyName.startsWith("focus-one-time-tier-")) {
    return AccessGroupType.OneTime;
  }
  throw new Error("Invalid access group key name");
};

export interface TierSchema {
  // The id could be missing if the tier is newly created on the client and not
  // saved to the database yet.
  associationId?: string | null;
  name: string;
  description: string;
  costUsdCents: number;
  coverImageURL: string;
  paymentCadenceDays: number;
  accessGroupKeyName: string;
  accessGroupDerivedPublicKey: string;
  e2e: boolean;
  numSubscribers: number;
  // A viewer has access if they are subscribed to the given tier or any higher tier for a creator.
  viewerHasAccess: boolean;
  viewerSubscriptionInfo: ViewerSubscriptionInfo | null;
}

interface ViewerSubscriptionInfo {
  associationId: string;
  status: SubscriptionStatus;
  nextPaymentDate: Date | null;
  derivedPublicKey: string;
  lastPaymentDate: Date | null;
}

const coerceSubscriptionExtraData = (
  extraData?: unknown | null,
): Record<string, string> => {
  if (!extraData || typeof extraData !== "object" || Array.isArray(extraData)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(extraData).filter(([, value]) => typeof value === "string"),
  );
};

const parseViewerSubscriptionInfo = ({
  viewerSubscriptionFragment,
  paymentCadenceDays,
}: {
  viewerSubscriptionFragment:
    | TierFragment["viewerSubscription"]["nodes"][number]
    | null;
  paymentCadenceDays: number;
}): ViewerSubscriptionInfo | null => {
  if (!viewerSubscriptionFragment?.associationId) {
    return null;
  }

  const viewerSubscriptionExtraData = coerceSubscriptionExtraData(
    viewerSubscriptionFragment.extraData,
  );
  const status = viewerSubscriptionExtraData.Status as
    | SubscriptionStatus
    | undefined;
  const derivedPublicKey =
    viewerSubscriptionExtraData.SubscriptionDerivedPublicKey;

  // Legacy subscription rows can exist without the newer metadata payload.
  // Treat those as absent viewer subscription details instead of constructing a
  // partially invalid object that breaks downstream consumers.
  if (!status || !derivedPublicKey) {
    return null;
  }

  const lastPaymentTimestamp =
    viewerSubscriptionExtraData.LatestPaymentTstampNanos
      ? Number(viewerSubscriptionExtraData.LatestPaymentTstampNanos) * 1000
      : null;

  return {
    associationId: viewerSubscriptionFragment.associationId,
    status,
    derivedPublicKey,
    nextPaymentDate: lastPaymentTimestamp
      ? getNextPaymentDate(lastPaymentTimestamp, paymentCadenceDays)
      : null,
    lastPaymentDate: lastPaymentTimestamp
      ? fdp.getLocalUTCDate(lastPaymentTimestamp).toDate()
      : null,
  };
};

export const parseTier = ({
  associationId,
  extraData,
  subscriptions,
  accessGroup,
  viewerSubscription,
}: TierFragment): TierSchema => {
  const viewerSubscriptionFragment = viewerSubscription?.nodes?.[0] ?? null;
  const paymentCadenceDays = Number(extraData.PaymentCadenceDays ?? 30);
  const viewerSubscriptionInfo = parseViewerSubscriptionInfo({
    viewerSubscriptionFragment,
    paymentCadenceDays,
  });

  return {
    associationId,
    name: extraData.Name,
    description: extraData.Description,
    coverImageURL: extraData.CoverImageURL,
    costUsdCents: Number(extraData.CostUsdCents),
    paymentCadenceDays,
    accessGroupKeyName: extraData.AccessGroupKeyName,
    accessGroupDerivedPublicKey: extraData.AGDerivedPublicKey,
    e2e: JSON.parse(extraData.E2E),
    numSubscribers: subscriptions?.totalCount ?? 0,
    // if the current viewer is subscribed to this tier, then viewerSubscriptionInfo will be populated. Otherwise, it
    // will be null.
    viewerSubscriptionInfo,
    viewerHasAccess:
      (accessGroup?.viewerAccessGroupMemberEntry?.totalCount ?? 0) > 0,
  };
};

export enum SubscriptionStatus {
  Active = "ACTIVE",
  ToBeCancelled = "TO_BE_CANCELLED",
  Cancelled = "CANCELLED",
}

export interface PaidContent {
  subscriber: CoreAccountFieldsFragment;
  associationId: string;
  startDate: Date;
  lastPaymentDate?: Date | null;
  nextPaymentDate?: Date | null;
  tier: TierSchema & { creator: CoreAccountFieldsFragment };
  derivedPublicKey: string;
  resourceURL?: string;
  status?: SubscriptionStatus;
}

export const getStatusText = (subscription: PaidContent) => {
  switch (subscription.status) {
    case SubscriptionStatus.Active:
      return subscription.tier.costUsdCents > 0
        ? "Next Billing Date"
        : "Active";
    case SubscriptionStatus.Cancelled:
      return "Canceled";
    case SubscriptionStatus.ToBeCancelled:
      return "Expires";
    default:
      return "Unknown Status";
  }
};

export const getNextPaymentDate = (
  lastPaymentTimestamp: number,
  paymentCadenceDays: number,
) => {
  return fdp
    .getLocalUTCDate(lastPaymentTimestamp)
    .add(paymentCadenceDays, "day")
    .toDate();
};

export const parsePaidContent = (
  subscription: PaidContentFragment,
): PaidContent => {
  if (!(subscription?.subscriptionTier && subscription?.subscriber)) {
    throw new Error("Invalid subscription data");
  }

  const tier = parseTier(subscription.subscriptionTier);

  if (
    !(subscription.subscriber && tier && subscription.subscriptionTier.owner)
  ) {
    throw new Error("Invalid subscription data");
  }

  const lastPaymentTimestamp: number | null = subscription.extraData
    .LatestPaymentTstampNanos
    ? Number(subscription.extraData.LatestPaymentTstampNanos) * 1000
    : null;

  let nextPaymentDate: Date | null = null;

  if (lastPaymentTimestamp && tier.paymentCadenceDays) {
    nextPaymentDate = getNextPaymentDate(
      lastPaymentTimestamp,
      tier.paymentCadenceDays,
    );
  }

  if (!subscription.createdAt) {
    throw new Error("No start date found.");
  }

  let resourceURL: string | undefined;
  if (subscription.extraData.PayToUnlockMessageId) {
    resourceURL = `/messages?messageId=${subscription.extraData.PayToUnlockMessageId}`;
  }
  if (subscription.extraData.PayToUnlockPostHash) {
    resourceURL = `/post/${subscription.extraData.PayToUnlockPostHash}`;
  }

  return {
    associationId: subscription.associationId,
    derivedPublicKey: subscription.extraData.SubscriptionDerivedPublicKey,
    subscriber: subscription.subscriber,
    startDate: fdp.getLocalUTCDate(subscription.createdAt).toDate(),
    nextPaymentDate,
    ...(resourceURL && { resourceURL }),
    tier: {
      ...tier,
      creator: subscription.subscriptionTier.owner,
    },
    status: subscription.extraData.Status as SubscriptionStatus,
  };
};

export const sortByCost = (a: TierSchema, b: TierSchema) =>
  a.costUsdCents - b.costUsdCents;

export const SUBSCRIPTION_DERIVED_KEY_EXPIRATION_DAYS = 36500; // 100 years, effectively infinite.
export const SUBSCRIPTION_TIER_ASSOCIATION_TYPE = "SUBSCRIPTION_TIER";

export const approveCreateTierDerivedKey = async (
  creatorPublicKey: string,
  derivedPublicKey: string,
) => {
  if (!process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY) {
    throw new Error(`${APP_NAME_GENERIC} app public key is not set.`);
  }

  return identity.derive(
    {
      GlobalDESOLimit: 0.1 * 1e9,
      TransactionCountLimitMap: {
        AUTHORIZE_DERIVED_KEY: 1,
        CREATE_USER_ASSOCIATION: "UNLIMITED",
        DELETE_USER_ASSOCIATION: "UNLIMITED",
        ACCESS_GROUP: "UNLIMITED",
        ACCESS_GROUP_MEMBERS: "UNLIMITED",
        NEW_MESSAGE: "UNLIMITED",
      },
      AccessGroupLimitMap: [
        {
          AccessGroupOwnerPublicKeyBase58Check: creatorPublicKey,
          ScopeType: "Any",
          AccessGroupKeyName: "",
          OperationType: "Any",
          OpCount: "UNLIMITED",
        },
      ],
      AccessGroupMemberLimitMap: [
        {
          AccessGroupOwnerPublicKeyBase58Check: creatorPublicKey,
          ScopeType: "Any",
          AccessGroupKeyName: "",
          OperationType: "Any",
          OpCount: "UNLIMITED",
        },
      ],
      AssociationLimitMap: [
        {
          AssociationClass: "User",
          AssociationType: SUBSCRIPTION_TIER_ASSOCIATION_TYPE,
          AppScopeType: "Scoped",
          AppPublicKeyBase58Check: process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY,
          AssociationOperation: "Create",
          OpCount: "UNLIMITED",
        },
        {
          AssociationClass: "User",
          AssociationType: SUBSCRIPTION_TIER_ASSOCIATION_TYPE,
          AppScopeType: "Scoped",
          AppPublicKeyBase58Check: process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY,
          AssociationOperation: "Delete",
          OpCount: "UNLIMITED",
        },
      ],
    },
    {
      ownerPublicKey: creatorPublicKey,
      derivedPublicKey: derivedPublicKey,
      expirationDays: SUBSCRIPTION_DERIVED_KEY_EXPIRATION_DAYS,
    },
  );
};

export const getSingleDerivedKey = async ({
  ownerPublicKey,
  derivedPublicKey,
}: {
  ownerPublicKey: string;
  derivedPublicKey: string;
}): Promise<UserDerivedKey | null> => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_CORE_DESO_NODE}/api/v0/get-single-derived-key/${ownerPublicKey}/${derivedPublicKey}`,
  );

  const data = await response.json();

  return data?.DerivedKey ?? null;
};

export const getSubscriptionTierCreatorKeyExpiredMessage = ({
  tierName,
}: {
  tierName?: string;
}) => {
  const tierLabel = tierName ? `"${tierName}"` : "This tier";
  return `${tierLabel} can't be purchased right now because the creator's tier access key expired. The creator needs to repair or re-save this tier in subscription settings before subscriptions can resume.`;
};

export const getSubscriptionTierCreatorKeyDeletedMessage = ({
  tierName,
}: {
  tierName?: string;
}) => {
  const tierLabel = tierName ? `"${tierName}"` : "This tier";
  return `${tierLabel} can't be repaired automatically because the previous creator access key was permanently deleted on-chain. This needs a different recovery path than a normal tier repair.`;
};

export class SubscriptionTierRepairError extends Error {
  rawError: string;

  constructor(message: string, rawError: string) {
    super(message);
    this.name = "SubscriptionTierRepairError";
    this.rawError = rawError;
  }
}

export const assertSubscriptionTierCreatorKeyIsActive = async ({
  creatorPublicKey,
  creatorDerivedPublicKey,
  tierName,
}: {
  creatorPublicKey: string;
  creatorDerivedPublicKey: string;
  tierName?: string;
}) => {
  const derivedKey = await getSingleDerivedKey({
    ownerPublicKey: creatorPublicKey,
    derivedPublicKey: creatorDerivedPublicKey,
  });

  if (derivedKey?.IsValid) {
    return derivedKey;
  }

  throw new Error(
    getSubscriptionTierCreatorKeyExpiredMessage({
      tierName,
    }),
  );
};

export const repairSubscriptionTierCreatorKey = async ({
  creatorPublicKey,
  tier,
}: {
  creatorPublicKey: string;
  tier: Pick<
    TierSchema,
    | "accessGroupKeyName"
    | "associationId"
    | "coverImageURL"
    | "description"
    | "name"
  >;
}) => {
  if (!tier.associationId) {
    throw new Error("Problem repairing tier. Association ID is missing.");
  }

  const { DerivedPublicKeyBase58Check } = await createDerivedKey({
    OwnerPublicKey: creatorPublicKey,
    DerivedKeyType: DerivedKeyType.SUBSCRIPTION_CREATOR,
    AccessGroupOwnerPublicKey: creatorPublicKey,
    AccessGroupKeyName: tier.accessGroupKeyName,
  });

  const resp = await approveCreateTierDerivedKey(
    creatorPublicKey,
    DerivedPublicKeyBase58Check,
  );

  try {
    await authorizeDerivedKeyAndWait({
      OwnerPublicKey: creatorPublicKey,
      DerivedPublicKey: resp.derivedPublicKeyBase58Check,
      ExpirationBlock: resp.expirationBlock,
      AccessSignature: resp.accessSignature,
      TransactionSpendingLimitHex: resp.transactionSpendingLimitHex,
    });
  } catch (error) {
    if (
      parseError(error).includes(
        "RuleErrorAuthorizeDerivedKeyDeletedDerivedPublicKey",
      )
    ) {
      throw new SubscriptionTierRepairError(
        getSubscriptionTierCreatorKeyDeletedMessage({
          tierName: tier.name,
        }),
        parseError(error),
      );
    }

    throw error;
  }

  const updateResp = await updateSubscriptionTier({
    CreatorPublicKey: creatorPublicKey,
    AssociationId: tier.associationId,
    Name: tier.name,
    Description: tier.description ?? "",
    CoverImageURL: tier.coverImageURL ?? "",
    DerivedPublicKey: DerivedPublicKeyBase58Check,
  });

  const txnHashHex = updateResp.associationTransaction.TxnHashHex;

  if (!txnHashHex) {
    throw new Error(
      "Tier repair update was submitted but no transaction hash was returned.",
    );
  }

  await waitForTxn({
    transactionHash: txnHashHex,
  });

  return resp.derivedPublicKeyBase58Check;
};

export const MONTHS_IN_3_YEARS = 36;

export const resolveSubscriptionPurchaseDerivedPublicKey = ({
  viewerSubscriptionInfo,
  newDerivedPublicKey,
}: {
  viewerSubscriptionInfo?: {
    derivedPublicKey?: string | null;
    status?: SubscriptionStatus | null;
  } | null;
  newDerivedPublicKey?: string | null;
}) => {
  return (
    newDerivedPublicKey || viewerSubscriptionInfo?.derivedPublicKey || null
  );
};

export const shouldReuseCancelledSubscriptionAssociation = (
  viewerSubscriptionInfo?: { status?: SubscriptionStatus | null } | null,
) => {
  return viewerSubscriptionInfo?.status === SubscriptionStatus.ToBeCancelled;
};

export const getSubscriptionSessionDerivedKeyAuthConfig = ({
  costUsdCents,
  usdCentsPerDesoExchangeRate,
}: {
  costUsdCents: number;
  usdCentsPerDesoExchangeRate: number;
}) => {
  const spendAmountDesoNanos =
    Math.ceil((costUsdCents / usdCentsPerDesoExchangeRate) * 1e9) +
    MIN_DESO_NANOS_FOR_SUBSCRIPTION_FEES;

  return {
    spendAmountDesoNanos,
    desoLimit: Math.max(5, spendAmountDesoNanos / 1e9),
  };
};

export const approveCreateSubscriptionDerivedKey = async (
  subscriberPublicKey: string,
  derivedPublicKey: string,
  usdCentsPerDesoExchangeRate: number,
  tier: Pick<TierSchema, "costUsdCents" | "accessGroupKeyName">,
) => {
  const tierValueInDeSo = tier.costUsdCents / usdCentsPerDesoExchangeRate;
  const threeYearsSubscriptionCostInDeSo = tierValueInDeSo * MONTHS_IN_3_YEARS;

  return await identity.derive(
    {
      // NOTE: If the price of DESO surges, this limit can be eaten up quickly.
      // We will need some mechanism for bumping it so that transactions executed on
      // the backend can still be processed.
      GlobalDESOLimit:
        Math.round(threeYearsSubscriptionCostInDeSo * 1e9) +
        MIN_DESO_NANOS_FOR_SUBSCRIPTION_FEES,
      TransactionCountLimitMap: {
        AUTHORIZE_DERIVED_KEY: 1,
        CREATE_USER_ASSOCIATION: 1,
        DELETE_USER_ASSOCIATION: 1,
        ACCESS_GROUP: "UNLIMITED",
        ACCESS_GROUP_MEMBERS: "UNLIMITED",
        NEW_MESSAGE: "UNLIMITED",
        BASIC_TRANSFER: "UNLIMITED",
        DAO_COIN_TRANSFER: "UNLIMITED",
      },
      DAOCoinOperationLimitMap: {
        [process.env.NEXT_PUBLIC_DUSD_PUBLIC_KEY]: {
          transfer: "UNLIMITED",
        },
        [process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY]: {
          transfer: "UNLIMITED",
        },
      },
      AssociationLimitMap: [
        {
          AssociationClass: "User",
          AssociationType: "SUBSCRIPTION",
          AppScopeType: "Scoped",
          AppPublicKeyBase58Check: process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY,
          AssociationOperation: "Create",
          OpCount: "UNLIMITED",
        },
        {
          AssociationClass: "User",
          AssociationType: "SUBSCRIPTION",
          AppScopeType: "Scoped",
          AppPublicKeyBase58Check: process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY,
          AssociationOperation: "Delete",
          OpCount: "UNLIMITED",
        },
      ],
    },
    {
      ownerPublicKey: subscriberPublicKey,
      derivedPublicKey,
      expirationDays: SUBSCRIPTION_DERIVED_KEY_EXPIRATION_DAYS,
    },
  );
};

export const getTotalSubscribersCount = (account: PartialAccount) => {
  if (!account) {
    return 0;
  }

  return (
    (account.subscriptionTiers?.nodes || [])?.reduce(
      (totalSubscribersCount, tier) => {
        return totalSubscribersCount + (tier?.subscriptions?.totalCount ?? 0);
      },
      0,
    ) ?? 0
  );
};
